import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Card, Collapse, Form, Modal, Table } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import BreadCrumbWrapper from 'components/global/BreadCrumbs/BreadCrumbs';
import SimpleTable from 'components/global/SimpleTable';
import dateOptions from 'components/global/Constant/dateOptions';
import {
  loanTrackingEventTypeDisplay,
  loanTrackingEventStatusDisplay,
} from 'components/global/Constant/constants';
import { enumCaseToTabCase } from 'libs/string';
import { getStatusStyles } from 'libs/functions/global.functions';
import {
  ascSortIcon,
  caretDownIcon,
  caretRightIcon,
  descSortIcon,
  sortIcon,
  timesIcon,
} from 'components/global/Constant/Icons';
import api from 'api/api';
import {
  fetchLoanProcessingBatchesManagePage,
  createLoanProcessingBatch,
  deleteLoanProcessingBatch,
  withdrawApplicationFromBatch,
  resetLoanProcessingBatch,
  patchLoanProcessingBatch,
  fetchBatchFundingMemoPreviewBase64,
  fetchBatchSigningSheetBase64,
  fetchBatchInitialsLogBase64,
  fetchBatchStepDocumentPreviewBase64,
  uploadBatchStepDocument,
  deleteBatchStepDocument,
  openLoanBatchStepDocumentInNewTab,
} from 'libs/loanProcessingBatchApi';
import { checkProgramPermissions } from 'libs/programAccessPermissions';
import alertSignal from 'signals/Alert.signal';
import Loader from 'components/global/Loader';
import CloseModalButton from 'components/global/Constant/CloseModalButton';
import userAccountSignal from 'signals/UserAccount.signal';
import { isEdoAdminOrOwner, canResetEntireLoanBatch, canUploadEdoLoanBatchStepAttachments, canDeleteEdoLoanBatchStepAttachment } from 'libs/edoPermissions';
import {
  notifyApplicationLoanTrackingRefresh,
  notifyLoanProcessingBatchesDashboardDirty,
  LOAN_PROCESSING_BATCHES_DASHBOARD_DIRTY_EVENT,
} from 'libs/loanProcessingBatchesDashboardDirty';
import DashboardTablePagination from 'components/views/Edo/EdoDashboard/DashboardTablePagination';
import LoanBatchRemovalNoteModal from 'components/views/Edo/LoanBatchRemovalNoteModal';

const BATCH_STATUS_LABELS = {
  DRAFT: 'Draft',
  PENDING_L1: 'Pending Management (L1)',
  PENDING_L2: 'Pending Management (L2)',
  PENDING_OUTGOING_RECEIPT: 'Outgoing — receipt',
  PENDING_TRUSTEE: 'Outgoing — trustee send',
  COMPLETED: 'Completed',
};

const PAGE_SIZE_OPTIONS = [10, 25, 50];

/** First sort direction when switching to a column (dates / loan count: newest or highest first). */
const BATCH_SORT_DEFAULT_DESC = new Set(['created', 'updated', 'loans']);

function BatchTableSortHeader({ label, sortKey, sortBy, sortDir, onSort }) {
  const active = sortBy === sortKey;
  const icon = active ? (sortDir === 'asc' ? ascSortIcon : descSortIcon) : sortIcon;
  return (
    <th
      role="columnheader"
      aria-sort={active ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
      className="align-middle"
      style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
      onClick={() => onSort(sortKey)}
    >
      <span className="align-middle">{label}</span>{' '}
      <FontAwesomeIcon icon={icon} className="ml-8 text-secondary" size="sm" />
    </th>
  );
}

const APPLICATION_HEADERS = [
  { key: 'loanStatus', title: 'Status', sortable: true },
  { key: 'createdDate', title: 'Date Submitted', sortable: true },
  { key: 'businessName', title: 'Business Name', sortable: true },
  { key: 'programName', title: 'Program Name', sortable: true },
  { key: 'lenderLoanNumber', title: 'Lender Loan #', sortable: true },
  { key: 'eventType', title: 'Event Type' },
  { key: 'status', title: 'Event Status' },
  { key: 'action', title: 'Action' },
];

const formatApplicationStatus = (status) =>
  status === 'IN_PROGRESS' ? 'Draft' : enumCaseToTabCase(status);

/**
 * @param {Array} list Application DTOs with events[0]
 * @param {{ memberCount: number, busy: boolean, onRequestRemove: (applicationId: number) => void } | null} removalOpts
 */
function applicationToRows(list, removalOpts = null) {
  const showRemoveMemberBtn =
    removalOpts && removalOpts.memberCount > 1 && typeof removalOpts.onRequestRemove === 'function';
  return list.map((app) => {
    const event = app.events?.[0];
    return {
      id: app.id,
      businessName: app.business?.name || `Application #${app.id}`,
      programName: app.fundingProgram?.name || '—',
      lenderLoanNumber: app.lenderLoanNumber != null && app.lenderLoanNumber !== '' ? app.lenderLoanNumber : '—',
      createdDate: (app.receivedDate ?? app.createdDate)
        ? new Date(app.receivedDate ?? app.createdDate).toLocaleString('en-US', dateOptions.date)
        : '—',
      loanStatusDisplay: app.status ? formatApplicationStatus(app.status) : '—',
      loanStatus: ({ headers, colIndex }) => (
        <td
          key={headers[colIndex]?.key}
          className={
            app.status ? `${getStatusStyles(app.status).class} align-middle` : 'align-middle'
          }
        >
          {app.status ? formatApplicationStatus(app.status) : '—'}
        </td>
      ),
      eventType: event?.eventCode
        ? loanTrackingEventTypeDisplay(event.eventCode, event.status)
        : '—',
      status: event?.status ? loanTrackingEventStatusDisplay(event.eventCode, event.status) : '—',
      action: ({ headers, colIndex }) => (
        <td key={headers[colIndex]?.key} className="text-right text-nowrap">
          <Link
            to={`/edo/portfolio/${app.id}?tab=loanTracking`}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-sm btn-outline-dark rounded"
          >
            View
          </Link>
          {showRemoveMemberBtn ? (
            <Button
              variant="outline-danger"
              size="sm"
              className="ml-8 rounded"
              disabled={removalOpts.busy}
              onClick={() => removalOpts.onRequestRemove(app.id)}
            >
              Remove from Batch
            </Button>
          ) : null}
        </td>
      ),
    };
  });
}

function membersToApplications(batch) {
  return (batch.members || []).map((m) => {
    if (m.application) return m.application;
    return {
      id: m.applicationId,
      business: m.businessName ? { name: m.businessName } : null,
      fundingProgram: null,
      lenderLoanNumber: m.lenderLoanNumber,
      status: null,
      receivedDate: null,
      createdDate: null,
      events: [],
    };
  });
}

function formatBatchDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-US', dateOptions.date);
}

function createdByLabel(batch) {
  const u = batch.createdBy;
  if (!u) return '—';
  if (u.displayName) return u.displayName;
  const parts = [u.firstName, u.lastName].filter(Boolean);
  return parts.length ? parts.join(' ') : '—';
}

function createPdfObjectUrlFromBase64(dataBase64) {
  if (!dataBase64) return null;
  const binary = atob(dataBase64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  const blob = new Blob([bytes], { type: 'application/pdf' });
  return URL.createObjectURL(blob);
}

const BATCH_DOC_PREVIEW_HEADING = {
  memo: 'Funding Memo',
  signing: 'Initial / Signing Sheet',
  initials: 'Combined Loan Tracking (Initials Log)',
  attachment: 'Batch Attachment',
};

const BATCH_DOC_PREVIEW_ERROR = {
  memo: 'Could not load Funding Memo preview.',
  signing: 'Could not load Initial / Signing Sheet preview.',
  initials: 'Could not load Combined Loan Tracking (Initials Log) preview.',
  attachment: 'Could not load batch attachment preview.',
};

const BATCH_ATTACHMENT_STEP_LABELS = {
  management_l1: 'Management L1',
  management_l2: 'Management L2',
  outgoing: 'Outgoing',
};

const BATCH_STEP_UPLOAD_LABEL = {
  management_l1: 'Upload Attachment (L1)',
  management_l2: 'Upload Attachment (L2)',
  outgoing: 'Upload Attachment (Outgoing)',
};

function batchAttachmentStepFromStatus(status) {
  if (status === 'PENDING_L1') return 'management_l1';
  if (status === 'PENDING_L2') return 'management_l2';
  if (status === 'PENDING_OUTGOING_RECEIPT' || status === 'PENDING_TRUSTEE') return 'outgoing';
  return null;
}

const EdoManageBatchProcessing = () => {
  const [page, setPage] = useState(1);
  const [listDirtyEpoch, setListDirtyEpoch] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
  const [total, setTotal] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [busyBatchId, setBusyBatchId] = useState(null);
  const [editingBatchNameId, setEditingBatchNameId] = useState(null);
  const [batchNameDraft, setBatchNameDraft] = useState('');
  const [savingBatchNameId, setSavingBatchNameId] = useState(null);
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
  const [pdfPreviewBatchId, setPdfPreviewBatchId] = useState(null);
  const [pdfPreviewKind, setPdfPreviewKind] = useState(null);
  const [pdfPreviewTitle, setPdfPreviewTitle] = useState('');
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  const [pdfPreviewLoading, setPdfPreviewLoading] = useState(false);
  const pdfPreviewBlobUrlRef = useRef(null);
  const [removalModal, setRemovalModal] = useState(null);
  const [showCreateBatchModal, setShowCreateBatchModal] = useState(false);
  const [createBatchName, setCreateBatchName] = useState('');
  const [createBatchProgramId, setCreateBatchProgramId] = useState('');
  const [createBatchPrograms, setCreateBatchPrograms] = useState([]);
  const [createBatchProgramsLoading, setCreateBatchProgramsLoading] = useState(false);
  const [createBatchBusy, setCreateBatchBusy] = useState(false);
  const [batchTableSort, setBatchTableSort] = useState({ key: 'updated', dir: 'desc' });
  const [deleteConfirmBatch, setDeleteConfirmBatch] = useState(null);
  const [filterProgramId, setFilterProgramId] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterCreatedById, setFilterCreatedById] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filterOptions, setFilterOptions] = useState({ programs: [], creators: [] });

  const userData = userAccountSignal.value?.userData;
  const canRemove = useMemo(() => {
    if (!userData) return false;
    if (isEdoAdminOrOwner(userData)) return true;
    return (userData.edoUserProgramAccesses || []).some((a) =>
      ['ADMIN', 'MANAGER', 'ANALYST', 'STAFF'].includes(a.role)
    );
  }, [userData]);

  const pdfPreviewBatchName = useMemo(() => {
    if (pdfPreviewBatchId == null) return '';
    const b = items.find((x) => x.id === pdfPreviewBatchId);
    return b?.name || '';
  }, [items, pdfPreviewBatchId]);

  const hasActiveListFilters = useMemo(
    () =>
      Boolean(filterProgramId) ||
      Boolean(filterStatus) ||
      Boolean(filterCreatedById) ||
      Boolean(debouncedSearch),
    [filterProgramId, filterStatus, filterCreatedById, debouncedSearch]
  );

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(searchInput.trim()), 300);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  const closePdfPreview = () => {
    setPdfPreviewOpen(false);
    setPdfPreviewLoading(false);
  };

  const handlePdfPreviewExited = () => {
    if (pdfPreviewBlobUrlRef.current) {
      URL.revokeObjectURL(pdfPreviewBlobUrlRef.current);
      pdfPreviewBlobUrlRef.current = null;
    }
    setPdfPreviewBatchId(null);
    setPdfPreviewKind(null);
    setPdfPreviewTitle('');
    setPdfPreviewUrl(null);
    setPdfPreviewLoading(false);
  };

  const openBatchDocPreview = (batchId, kind) => {
    setPdfPreviewBatchId(batchId);
    setPdfPreviewKind(kind);
    setPdfPreviewTitle('');
    setPdfPreviewOpen(true);
    setPdfPreviewUrl(null);
    setPdfPreviewLoading(true);
    void (async () => {
      try {
        const raw =
          kind === 'signing'
            ? await fetchBatchSigningSheetBase64(batchId)
            : kind === 'memo'
              ? await fetchBatchFundingMemoPreviewBase64(batchId)
              : await fetchBatchInitialsLogBase64(batchId);
        const b64 = typeof raw === 'string' ? raw : raw?.data;
        if (!b64 || typeof b64 !== 'string') {
          throw new Error('Empty preview payload');
        }
        const objectUrl = createPdfObjectUrlFromBase64(b64);
        if (!objectUrl) {
          throw new Error('Could not build preview');
        }
        if (pdfPreviewBlobUrlRef.current) {
          URL.revokeObjectURL(pdfPreviewBlobUrlRef.current);
        }
        pdfPreviewBlobUrlRef.current = objectUrl;
        setPdfPreviewUrl(objectUrl);
      } catch (err) {
        window.alert(err?.message || BATCH_DOC_PREVIEW_ERROR[kind]);
        closePdfPreview();
      } finally {
        setPdfPreviewLoading(false);
      }
    })();
  };

  const openBatchAttachmentPreview = (batchId, doc) => {
    setPdfPreviewBatchId(batchId);
    setPdfPreviewKind('attachment');
    setPdfPreviewTitle(doc?.fileName || `Attachment #${doc?.id || ''}`);
    setPdfPreviewOpen(true);
    setPdfPreviewUrl(null);
    setPdfPreviewLoading(true);
    void (async () => {
      try {
        const raw = await fetchBatchStepDocumentPreviewBase64(batchId, doc.id);
        const b64 = typeof raw === 'string' ? raw : raw?.data;
        if (!b64 || typeof b64 !== 'string') {
          throw new Error('Empty preview payload');
        }
        const objectUrl = createPdfObjectUrlFromBase64(b64);
        if (!objectUrl) {
          throw new Error('Could not build preview');
        }
        if (pdfPreviewBlobUrlRef.current) {
          URL.revokeObjectURL(pdfPreviewBlobUrlRef.current);
        }
        pdfPreviewBlobUrlRef.current = objectUrl;
        setPdfPreviewUrl(objectUrl);
      } catch (err) {
        window.alert(err?.message || BATCH_DOC_PREVIEW_ERROR.attachment);
        closePdfPreview();
      } finally {
        setPdfPreviewLoading(false);
      }
    })();
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchLoanProcessingBatchesManagePage({
        page,
        pageSize,
        sortBy: batchTableSort.key,
        sortDir: batchTableSort.dir,
        fundingProgramId: filterProgramId || undefined,
        status: filterStatus || undefined,
        createdById: filterCreatedById || undefined,
        search: debouncedSearch || undefined,
      });
      setItems(Array.isArray(res?.items) ? res.items : []);
      setTotal(typeof res?.total === 'number' ? res.total : 0);
      if (res?.filterOptions && typeof res.filterOptions === 'object') {
        setFilterOptions({
          programs: Array.isArray(res.filterOptions.programs) ? res.filterOptions.programs : [],
          creators: Array.isArray(res.filterOptions.creators) ? res.filterOptions.creators : [],
        });
      }
    } catch (e) {
      setItems([]);
      setTotal(0);
      setError(e?.message || 'Failed to load batches.');
    } finally {
      setLoading(false);
    }
  }, [
    page,
    pageSize,
    batchTableSort.key,
    batchTableSort.dir,
    filterProgramId,
    filterStatus,
    filterCreatedById,
    debouncedSearch,
    listDirtyEpoch,
  ]);

  useEffect(() => {
    const onDirty = () => {
      setPage(1);
      setListDirtyEpoch((e) => e + 1);
    };
    window.addEventListener(LOAN_PROCESSING_BATCHES_DASHBOARD_DIRTY_EVENT, onDirty);
    return () => window.removeEventListener(LOAN_PROCESSING_BATCHES_DASHBOARD_DIRTY_EVENT, onDirty);
  }, []);

  const handleBatchTableSort = useCallback((key) => {
    setPage(1);
    setBatchTableSort((prev) => {
      if (prev.key === key) {
        return { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
      }
      return { key, dir: BATCH_SORT_DEFAULT_DESC.has(key) ? 'desc' : 'asc' };
    });
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const toggleExpanded = (batchId) => {
    setExpanded((prev) => ({ ...prev, [batchId]: !prev[batchId] }));
  };

  const batchColSpan = 9;

  const confirmBatchRemoval = useCallback(
    async (note) => {
      if (!removalModal) return;
      const { batchId, variant, applicationId } = removalModal;
      setBusyBatchId(batchId);
      try {
        if (variant === 'withdrawOne') {
          await withdrawApplicationFromBatch(batchId, applicationId, note);
          notifyApplicationLoanTrackingRefresh({ applicationId });
        } else {
          const batchRow = items.find((x) => x.id === batchId);
          const applicationIds = (batchRow?.members || []).map((m) => m.applicationId);
          await resetLoanProcessingBatch(batchId, note);
          notifyApplicationLoanTrackingRefresh({ applicationIds });
        }
        notifyLoanProcessingBatchesDashboardDirty();
        setRemovalModal(null);
        await load();
      } catch (e) {
        window.alert(e?.message || (variant === 'resetAll' ? 'Reset Batch failed' : 'Remove failed'));
      } finally {
        setBusyBatchId(null);
      }
    },
    [removalModal, load, items]
  );

  const handleDeleteBatchAttachment = useCallback(
    async (batchId, documentId) => {
      setBusyBatchId(batchId);
      try {
        await deleteBatchStepDocument(batchId, documentId);
        notifyLoanProcessingBatchesDashboardDirty();
        const batchRow = items.find((x) => x.id === batchId);
        const applicationIds = (batchRow?.members || []).map((m) => m.applicationId);
        notifyApplicationLoanTrackingRefresh({ applicationIds });
        await load();
      } catch (e) {
        window.alert(e?.message || 'Could not delete attachment');
      } finally {
        setBusyBatchId(null);
      }
    },
    [items, load]
  );

  const handleUploadBatchAttachment = useCallback(
    async (batch, file) => {
      const step = batchAttachmentStepFromStatus(batch.status);
      if (!step || !file) return;
      const reader = new FileReader();
      reader.onload = async () => {
        const result = reader.result;
        if (typeof result !== 'string') return;
        const base64 = result.split(',')[1] || result;
        setBusyBatchId(batch.id);
        try {
          await uploadBatchStepDocument(batch.id, {
            step,
            fileBase64: base64,
            fileName: file.name,
            fileType: file.type,
          });
          notifyLoanProcessingBatchesDashboardDirty();
          const applicationIds = (batch.members || []).map((m) => m.applicationId);
          notifyApplicationLoanTrackingRefresh({ applicationIds });
          await load();
        } catch (e) {
          window.alert(e?.message || 'Could not upload attachment');
        } finally {
          setBusyBatchId(null);
        }
      };
      reader.readAsDataURL(file);
    },
    [load]
  );

  const startEditingBatchName = useCallback((batch) => {
    setEditingBatchNameId(batch.id);
    setBatchNameDraft(batch.name || '');
  }, []);

  const cancelEditingBatchName = useCallback(() => {
    setEditingBatchNameId(null);
    setBatchNameDraft('');
  }, []);

  const saveBatchName = useCallback(
    async (batchId) => {
      const trimmed = String(batchNameDraft || '').trim();
      if (!trimmed) {
        window.alert('Batch name is required.');
        return;
      }
      setSavingBatchNameId(batchId);
      try {
        await patchLoanProcessingBatch(batchId, { name: trimmed });
        cancelEditingBatchName();
        await load();
      } catch (e) {
        window.alert(e?.message || 'Could not save batch name');
      } finally {
        setSavingBatchNameId(null);
      }
    },
    [batchNameDraft, cancelEditingBatchName, load]
  );

  const removalModalBusy = !!(removalModal && busyBatchId === removalModal.batchId);

  const loadProgramsForCreateBatch = useCallback(async () => {
    const ud = userAccountSignal.value?.userData;
    const edoId = ud?.account?.edoId ?? ud?.account?.edo?.id;
    setCreateBatchProgramsLoading(true);
    setCreateBatchPrograms([]);
    if (!edoId) {
      setCreateBatchProgramsLoading(false);
      return;
    }
    try {
      const fundingPrograms = await api.get({
        path: '/fundingPrograms',
        options: { where: { edoId } },
      });
      const list = Array.isArray(fundingPrograms) ? fundingPrograms : [];
      const mapRow = (p) => ({ id: p.id, name: p.name || `Program #${p.id}` });
      /** Matches backend loan batch create: Owner/Admin/Super Admin bypass per-program EUPA + canInsert. */
      let eligible;
      if (isEdoAdminOrOwner(ud)) {
        eligible = list.map(mapRow).sort((a, b) => a.name.localeCompare(b.name));
      } else {
        const responses = await Promise.all(list.map((p) => checkProgramPermissions(p.id)));
        eligible = list
          .filter((_, i) => responses[i]?.permissions?.canInsert === true)
          .map(mapRow)
          .sort((a, b) => a.name.localeCompare(b.name));
      }
      setCreateBatchPrograms(eligible);
      if (eligible.length === 1) {
        setCreateBatchProgramId(String(eligible[0].id));
      }
    } catch {
      setCreateBatchPrograms([]);
    } finally {
      setCreateBatchProgramsLoading(false);
    }
  }, []);

  const openCreateBatchModal = () => {
    setCreateBatchName('');
    setCreateBatchProgramId('');
    setShowCreateBatchModal(true);
    void loadProgramsForCreateBatch();
  };

  const closeCreateBatchModal = () => {
    if (createBatchBusy) return;
    setShowCreateBatchModal(false);
  };

  const handleCreateBatchFromManagePage = async () => {
    const name = createBatchName.trim();
    const pid = parseInt(createBatchProgramId, 10);
    if (!name || !Number.isFinite(pid)) return;
    setCreateBatchBusy(true);
    try {
      await createLoanProcessingBatch({ name, fundingProgramId: pid });
      alertSignal.update({
        type: 'notification',
        message: 'Batch created.',
        variant: 'success',
      });
      notifyLoanProcessingBatchesDashboardDirty();
      setShowCreateBatchModal(false);
      setCreateBatchName('');
      setCreateBatchProgramId('');
      await load();
    } catch (e) {
      window.alert(e?.message || 'Could not create batch');
    } finally {
      setCreateBatchBusy(false);
    }
  };

  const closeDeleteConfirmModal = () => {
    if (deleteConfirmBatch != null && busyBatchId === deleteConfirmBatch.id) return;
    setDeleteConfirmBatch(null);
  };

  const handleConfirmDeleteBatch = async () => {
    if (!deleteConfirmBatch) return;
    setBusyBatchId(deleteConfirmBatch.id);
    try {
      await deleteLoanProcessingBatch(deleteConfirmBatch.id);
      alertSignal.update({
        type: 'notification',
        message: 'Batch deleted.',
        variant: 'success',
      });
      notifyLoanProcessingBatchesDashboardDirty();
      setExpanded((prev) => {
        const next = { ...prev };
        delete next[deleteConfirmBatch.id];
        return next;
      });
      setDeleteConfirmBatch(null);
      await load();
    } catch (e) {
      window.alert(e?.message || 'Could not delete batch');
    } finally {
      setBusyBatchId(null);
    }
  };

  return (
    <BreadCrumbWrapper>
      <Card className="rounded-0">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center flex-wrap mb-16">
            <p className="text-muted small mb-0">
              Loan processing batches for programs you can access. Expand a row to see applications.
            </p>
            <p className="text-muted small mb-0 ml-md-16">
              Total Results: {total}
            </p>
          </div>

          <div className="d-flex flex-wrap align-items-center mb-16">
            <Form.Group controlId="manage-batch-page-size" className="mb-0 mr-12">
              <Form.Label className="sr-only">Rows per Page</Form.Label>
              <Form.Control
                as="select"
                size="sm"
                style={{ minWidth: '8rem' }}
                value={String(pageSize)}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
                aria-label="Batches per Page"
              >
                {PAGE_SIZE_OPTIONS.map((n) => (
                  <option key={n} value={n}>
                    {n} per Page
                  </option>
                ))}
              </Form.Control>
            </Form.Group>
            <Form.Group controlId="manage-batch-filter-program" className="mb-0 mr-12">
              <Form.Label className="sr-only">Program</Form.Label>
              <Form.Control
                as="select"
                size="sm"
                style={{ minWidth: '9rem' }}
                value={filterProgramId}
                onChange={(e) => {
                  setFilterProgramId(e.target.value);
                  setPage(1);
                }}
                aria-label="Filter by Program"
              >
                <option value="">All Programs</option>
                {filterOptions.programs.map((p) => (
                  <option key={p.id} value={String(p.id)}>
                    {p.name}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>
            <Form.Group controlId="manage-batch-filter-status" className="mb-0 mr-12">
              <Form.Label className="sr-only">Status</Form.Label>
              <Form.Control
                as="select"
                size="sm"
                style={{ minWidth: '9rem' }}
                value={filterStatus}
                onChange={(e) => {
                  setFilterStatus(e.target.value);
                  setPage(1);
                }}
                aria-label="Filter by Batch Status"
              >
                <option value="">All Statuses</option>
                {Object.entries(BATCH_STATUS_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>
            <Form.Group controlId="manage-batch-filter-created-by" className="mb-0 mr-12">
              <Form.Label className="sr-only">Created by</Form.Label>
              <Form.Control
                as="select"
                size="sm"
                style={{ minWidth: '10rem' }}
                value={filterCreatedById}
                onChange={(e) => {
                  setFilterCreatedById(e.target.value);
                  setPage(1);
                }}
                aria-label="Filter by Creator"
              >
                <option value="">All Creators</option>
                {filterOptions.creators.map((c) => (
                  <option key={c.id} value={String(c.id)}>
                    {c.displayName}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>
            <Form.Group controlId="manage-batch-search" className="mb-0 mr-12 flex-grow-1" style={{ minWidth: '12rem', maxWidth: '22rem' }}>
              <Form.Label className="sr-only">Search</Form.Label>
              <Form.Control
                type="search"
                size="sm"
                placeholder="Search Batches, Business Name, Loan #…"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                aria-label="Search Batches and Applications"
              />
            </Form.Group>
            <Button
              variant="primary"
              className="rounded ml-md-auto"
              size="sm"
              type="button"
              onClick={openCreateBatchModal}
              disabled={loading}
            >
              Create Batch
            </Button>
          </div>

          {error ? <p className="text-danger mb-0">{error}</p> : null}

          {loading ? (
            <Loader className="py-24" message="Loading batches…" />
          ) : !items.length && !error ? (
            <p className="text-muted mb-0">
              {hasActiveListFilters
                ? 'No loan processing batches match your filters or search.'
                : 'No loan processing batches found.'}
            </p>
          ) : (
            <>
              <div className="extend-table-container no-padding table-responsive">
                <Table bordered className="mb-0">
                  <thead className="bg-light">
                    <tr>
                      <th style={{ width: '2.5rem' }} aria-label="Expand" />
                      <BatchTableSortHeader
                        label="Batch Name"
                        sortKey="name"
                        sortBy={batchTableSort.key}
                        sortDir={batchTableSort.dir}
                        onSort={handleBatchTableSort}
                      />
                      <BatchTableSortHeader
                        label="Program"
                        sortKey="program"
                        sortBy={batchTableSort.key}
                        sortDir={batchTableSort.dir}
                        onSort={handleBatchTableSort}
                      />
                      <BatchTableSortHeader
                        label="Status"
                        sortKey="status"
                        sortBy={batchTableSort.key}
                        sortDir={batchTableSort.dir}
                        onSort={handleBatchTableSort}
                      />
                      <BatchTableSortHeader
                        label="Loans"
                        sortKey="loans"
                        sortBy={batchTableSort.key}
                        sortDir={batchTableSort.dir}
                        onSort={handleBatchTableSort}
                      />
                      <BatchTableSortHeader
                        label="Funding Memo"
                        sortKey="fundingMemo"
                        sortBy={batchTableSort.key}
                        sortDir={batchTableSort.dir}
                        onSort={handleBatchTableSort}
                      />
                      <BatchTableSortHeader
                        label="Created by"
                        sortKey="createdBy"
                        sortBy={batchTableSort.key}
                        sortDir={batchTableSort.dir}
                        onSort={handleBatchTableSort}
                      />
                      <BatchTableSortHeader
                        label="Created"
                        sortKey="created"
                        sortBy={batchTableSort.key}
                        sortDir={batchTableSort.dir}
                        onSort={handleBatchTableSort}
                      />
                      <BatchTableSortHeader
                        label="Updated"
                        sortKey="updated"
                        sortBy={batchTableSort.key}
                        sortDir={batchTableSort.dir}
                        onSort={handleBatchTableSort}
                      />
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((batch) => {
                      const isOpen = !!expanded[batch.id];
                      const allowMutateBatchMembers =
                        canRemove && batch.status !== 'COMPLETED';
                      const currentAttachmentStep = batchAttachmentStepFromStatus(batch.status);
                      const canUploadBatchAttachments =
                        currentAttachmentStep != null &&
                        canUploadEdoLoanBatchStepAttachments(userData, batch);
                      const canDeleteBatchAttachment = () =>
                        canDeleteEdoLoanBatchStepAttachment(userData, batch);
                      const busy = busyBatchId === batch.id;
                      const renaming = savingBatchNameId === batch.id;
                      const isEditingName = editingBatchNameId === batch.id;
                      const apps = membersToApplications(batch);
                      const removalOpts = allowMutateBatchMembers
                        ? {
                            memberCount: batch.memberCount,
                            busy,
                            onRequestRemove: (applicationId) =>
                              setRemovalModal({ batchId: batch.id, variant: 'withdrawOne', applicationId }),
                          }
                        : null;
                      const tableRows = applicationToRows(apps, removalOpts);
                      return (
                        <Fragment key={batch.id}>
                          <tr>
                            <td className="align-middle">
                              <button
                                type="button"
                                className="btn btn-link btn-sm p-0"
                                onClick={() => toggleExpanded(batch.id)}
                                aria-expanded={isOpen}
                                aria-label={isOpen ? 'Collapse applications' : 'Expand applications'}
                              >
                                <FontAwesomeIcon
                                  icon={isOpen ? caretDownIcon : caretRightIcon}
                                  className="text-secondary"
                                />
                              </button>
                            </td>
                            <td className="align-middle">
                              {batch.status === 'COMPLETED' ? (
                                <span className="font-weight-bold">{batch.name}</span>
                              ) : isEditingName ? (
                                <div className="d-inline-flex align-items-stretch flex-nowrap">
                                  <Form.Control
                                    size="sm"
                                    className="align-self-center mr-8 flex-shrink-1"
                                    style={{
                                      width: '14rem',
                                      maxWidth: 'min(24rem, calc(100vw - 12rem))',
                                      minWidth: '10rem',
                                    }}
                                    value={batchNameDraft}
                                    disabled={renaming}
                                    onChange={(e) => setBatchNameDraft(e.target.value)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Escape') cancelEditingBatchName();
                                      if (e.key === 'Enter') {
                                        e.preventDefault();
                                        void saveBatchName(batch.id);
                                      }
                                    }}
                                    aria-label="Batch Name"
                                  />
                                  <Button
                                    variant="primary"
                                    size="sm"
                                    type="button"
                                    className="mr-8 text-nowrap shadow-none d-flex align-items-center justify-content-center"
                                    disabled={renaming}
                                    onClick={() => void saveBatchName(batch.id)}
                                  >
                                    Save
                                  </Button>
                                  <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    type="button"
                                    className="shadow-none px-8 d-flex align-items-center justify-content-center"
                                    disabled={renaming}
                                    onClick={cancelEditingBatchName}
                                    title="Cancel"
                                    aria-label="Cancel Editing Batch Name"
                                  >
                                    <FontAwesomeIcon icon={timesIcon} />
                                  </Button>
                                </div>
                              ) : (
                                <div className="d-flex align-items-center flex-wrap">
                                  <button
                                    type="button"
                                    className="btn btn-link p-0 text-left font-weight-bold text-body"
                                    title="Click to Edit Batch Name"
                                    onClick={() => startEditingBatchName(batch)}
                                  >
                                    {batch.name}
                                  </button>
                                </div>
                              )}
                            </td>
                            <td className="align-middle">{batch.fundingProgram?.name || '—'}</td>
                            <td className="align-middle">
                              {BATCH_STATUS_LABELS[batch.status] || batch.status}
                            </td>
                            <td className="align-middle">{batch.memberCount}</td>
                            <td className="align-middle">
                              {batch.hasFundingMemo ? 'Yes' : '—'}
                            </td>
                            <td className="align-middle">{createdByLabel(batch)}</td>
                            <td className="align-middle">{formatBatchDate(batch.createdDate)}</td>
                            <td className="align-middle">{formatBatchDate(batch.updatedDate)}</td>
                          </tr>
                          <tr className="bg-white">
                            <td colSpan={batchColSpan} className="p-0 border-top-0">
                              <Collapse in={isOpen}>
                                <div className="px-16 py-16 bg-light border-top">
                                  {(batch.hasFundingMemo || batch.hasSigningSheet || batch.hasInitialsLog) && (
                                    <div className="small mb-12">
                                      <span className="font-weight-bold text-muted mr-8">Generated Documents:</span>
                                      {batch.hasFundingMemo ? (
                                        <Button
                                          variant="link"
                                          size="sm"
                                          className="p-0 mr-12 align-baseline"
                                          onClick={() => openBatchDocPreview(batch.id, 'memo')}
                                        >
                                          Funding Memo
                                        </Button>
                                      ) : null}
                                      {batch.hasSigningSheet ? (
                                        <Button
                                          variant="link"
                                          size="sm"
                                          className="p-0 mr-12 align-baseline"
                                          onClick={() => openBatchDocPreview(batch.id, 'signing')}
                                        >
                                          Initial / Signing Sheet
                                        </Button>
                                      ) : null}
                                      {batch.hasInitialsLog ? (
                                        <Button
                                          variant="link"
                                          size="sm"
                                          className="p-0 align-baseline"
                                          onClick={() => openBatchDocPreview(batch.id, 'initials')}
                                        >
                                          Loan Tracking Initials
                                        </Button>
                                      ) : null}
                                    </div>
                                  )}
                                  <div className="border rounded p-8 mb-12">
                                    <div className="small text-muted">
                                      <span className="font-weight-bold">Additional Attachments</span>
                                    </div>
                                    {canUploadBatchAttachments ? (
                                      <div className="d-flex flex-wrap align-items-center mt-2 mb-0">
                                        <label className="btn btn-sm btn-outline-secondary mr-8 mb-0">
                                          {busy ? 'Uploading…' : BATCH_STEP_UPLOAD_LABEL[currentAttachmentStep]}
                                          <input
                                            type="file"
                                            className="d-none"
                                            disabled={busy}
                                            onChange={(e) => {
                                              const f = e.target.files?.[0];
                                              if (f) {
                                                void handleUploadBatchAttachment(batch, f);
                                              }
                                              e.target.value = '';
                                            }}
                                          />
                                        </label>
                                      </div>
                                    ) : null}
                                    {!batch.documents || batch.documents.length === 0 ? (
                                      <div className="small text-muted mt-2 mb-0">No Batch Attachments Uploaded Yet.</div>
                                    ) : (
                                      <div className="mt-8">
                                        {batch.documents.map((doc) => (
                                          <div
                                            key={doc.id}
                                            className="d-flex flex-wrap align-items-center justify-content-between border-top pt-8 mt-8"
                                          >
                                            <div className="small text-dark mr-8">
                                              {doc.fileName || `Attachment #${doc.id}`}
                                              <span className="text-muted ml-8">
                                                ({BATCH_ATTACHMENT_STEP_LABELS[doc.stepKey] || doc.stepKey || 'Unknown Step'})
                                              </span>
                                            </div>
                                            <div className="d-flex align-items-center">
                                              <Button
                                                variant="outline-secondary"
                                                size="sm"
                                                className="mr-8"
                                                type="button"
                                                onClick={() => openBatchAttachmentPreview(batch.id, doc)}
                                              >
                                                Preview
                                              </Button>
                                              <Button
                                                variant="outline-secondary"
                                                size="sm"
                                                type="button"
                                                onClick={() => openLoanBatchStepDocumentInNewTab(batch.id, doc.id)}
                                              >
                                                Download
                                              </Button>
                                              {canDeleteBatchAttachment() ? (
                                                <Button
                                                  variant="outline-danger"
                                                  size="sm"
                                                  className="ml-8"
                                                  type="button"
                                                  disabled={busy}
                                                  onClick={() =>
                                                    void handleDeleteBatchAttachment(batch.id, doc.id)
                                                  }
                                                >
                                                  Delete
                                                </Button>
                                              ) : null}
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                  <div className="d-flex flex-wrap align-items-center justify-content-between mb-8">
                                    <div className="small font-weight-bold text-muted mb-0">Applications in this batch</div>
                                    {canRemove ? (
                                      <div className="d-flex flex-wrap align-items-center">
                                        {canResetEntireLoanBatch(userData, batch) ? (
                                        <Button
                                          variant="outline-danger"
                                          size="sm"
                                          className="mr-8"
                                          disabled={busy || !allowMutateBatchMembers}
                                          title={
                                            batch.status === 'COMPLETED'
                                              ? 'A completed batch cannot be reset.'
                                              : undefined
                                          }
                                          onClick={() =>
                                            setRemovalModal({ batchId: batch.id, variant: 'resetAll' })
                                          }
                                        >
                                          Reset Batch (All Applications)
                                        </Button>
                                        ) : null}
                                        <Button
                                          variant="outline-danger"
                                          size="sm"
                                          type="button"
                                          className="rounded"
                                          disabled={busy || batch.memberCount > 0}
                                          title={
                                            batch.memberCount > 0
                                              ? 'Remove all applications from this batch before deleting it.'
                                              : 'Delete this batch'
                                          }
                                          onClick={() =>
                                            setDeleteConfirmBatch({
                                              id: batch.id,
                                              name: batch.name || `Batch ${batch.id}`,
                                            })
                                          }
                                        >
                                          Delete Batch
                                        </Button>
                                      </div>
                                    ) : null}
                                  </div>
                                  {tableRows.length ? (
                                    <SimpleTable
                                      headers={APPLICATION_HEADERS}
                                      rows={tableRows}
                                      tableProps={{ className: 'mb-0 bg-white' }}
                                    />
                                  ) : (
                                    <p className="text-muted small mb-0">No application details available.</p>
                                  )}
                                </div>
                              </Collapse>
                            </td>
                          </tr>
                        </Fragment>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
              <DashboardTablePagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
            </>
          )}
        </Card.Body>
      </Card>
      <Modal show={showCreateBatchModal} onHide={closeCreateBatchModal} centered>
        <Modal.Header className="d-flex align-items-center justify-content-between">
          <Modal.Title className="mb-0">Create Batch</Modal.Title>
          <CloseModalButton
            onCloseModal={() => {
              if (!createBatchBusy) closeCreateBatchModal();
            }}
          />
        </Modal.Header>
        <Modal.Body>
          {createBatchProgramsLoading ? (
            <p className="text-muted mb-0">Loading funding programs…</p>
          ) : createBatchPrograms.length === 0 ? (
            <p className="text-muted mb-0">
              You do not have permission to create batches for any funding program, or no programs were found for
              your organization.
            </p>
          ) : (
            <>
              <Form.Group className="mb-3">
                <Form.Label>Funding Program</Form.Label>
                <Form.Control
                  as="select"
                  value={createBatchProgramId}
                  onChange={(e) => setCreateBatchProgramId(e.target.value)}
                  disabled={createBatchBusy}
                >
                  <option value="">Select Program…</option>
                  {createBatchPrograms.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </Form.Control>
              </Form.Group>
              <Form.Group className="mb-0">
                <Form.Label>Batch Name</Form.Label>
                <Form.Control
                  value={createBatchName}
                  onChange={(e) => setCreateBatchName(e.target.value)}
                  placeholder="e.g. TJH 03.23.26 #1 SB Loans"
                  autoFocus
                  disabled={createBatchBusy}
                />
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" className="rounded" onClick={closeCreateBatchModal} disabled={createBatchBusy}>
            Cancel
          </Button>
          <Button
            variant="primary"
            className="rounded"
            disabled={
              createBatchBusy ||
              createBatchProgramsLoading ||
              createBatchPrograms.length === 0 ||
              !createBatchName.trim() ||
              !createBatchProgramId
            }
            onClick={handleCreateBatchFromManagePage}
          >
            {createBatchBusy ? 'Creating…' : 'Create'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={deleteConfirmBatch != null} onHide={closeDeleteConfirmModal} centered>
        <Modal.Header className="d-flex align-items-center justify-content-between">
          <Modal.Title className="mb-0">Delete Batch?</Modal.Title>
          <CloseModalButton
            onCloseModal={() => {
              closeDeleteConfirmModal();
            }}
          />
        </Modal.Header>
        <Modal.Body>
          <p className="mb-0">
            Are you sure you want to delete{' '}
            <strong>{deleteConfirmBatch?.name}</strong>? This cannot be undone.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            className="rounded"
            type="button"
            onClick={closeDeleteConfirmModal}
            disabled={deleteConfirmBatch != null && busyBatchId === deleteConfirmBatch.id}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            className="rounded"
            type="button"
            onClick={() => void handleConfirmDeleteBatch()}
            disabled={deleteConfirmBatch != null && busyBatchId === deleteConfirmBatch.id}
          >
            {deleteConfirmBatch != null && busyBatchId === deleteConfirmBatch.id ? 'Deleting…' : 'Delete'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={pdfPreviewOpen}
        onHide={closePdfPreview}
        onExited={handlePdfPreviewExited}
        size="xl"
        centered
        scrollable
      >
        <Modal.Header className="d-flex align-items-center justify-content-between">
          <Modal.Title
            className="text-truncate pr-16 mb-0 flex-grow-1"
            style={{ minWidth: 0 }}
            title={pdfPreviewBatchName}
          >
            {pdfPreviewKind && pdfPreviewBatchId != null
              ? `${BATCH_DOC_PREVIEW_HEADING[pdfPreviewKind]} — ${
                  pdfPreviewKind === 'attachment' && pdfPreviewTitle
                    ? pdfPreviewTitle
                    : pdfPreviewBatchName
                }`
              : ''}
          </Modal.Title>
          <CloseModalButton onCloseModal={closePdfPreview} />
        </Modal.Header>
        <Modal.Body className="p-0 bg-light" style={{ minHeight: '70vh' }}>
          {pdfPreviewLoading ? (
            <Loader className="py-48" message="Loading Preview…" />
          ) : pdfPreviewUrl ? (
            <iframe
              title={
                pdfPreviewKind && pdfPreviewBatchId != null
                  ? `${BATCH_DOC_PREVIEW_HEADING[pdfPreviewKind]} Preview — ${
                      pdfPreviewKind === 'attachment' && pdfPreviewTitle
                        ? pdfPreviewTitle
                        : `Batch ${pdfPreviewBatchId}`
                    }`
                  : 'PDF preview'
              }
              src={pdfPreviewUrl}
              className="d-block w-100 border-0 bg-white"
              style={{ minHeight: '70vh', height: '70vh' }}
            />
          ) : null}
        </Modal.Body>
        <Modal.Footer className="justify-content-end">
          <Button variant="primary" size="sm" type="button" onClick={closePdfPreview}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
      <LoanBatchRemovalNoteModal
        show={!!removalModal}
        variant={removalModal?.variant === 'resetAll' ? 'resetAll' : 'withdrawOne'}
        onHide={() => {
          if (!removalModalBusy) setRemovalModal(null);
        }}
        onConfirm={confirmBatchRemoval}
        busy={removalModalBusy}
      />
    </BreadCrumbWrapper>
  );
};

export default EdoManageBatchProcessing;
